// router/index.js - Time-based loading (shows only if navigation > 250ms)

import {
  route
} from "quasar/wrappers";
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
} from "vue-router";
import {
  LoadingBar,
  Notify
} from "quasar";
import routes from "./routes";
import {
  routePreloader
} from "./routePreloader";
import { Sentry } from "../boot/sentry";
import { useGlobalTheme } from "../composables/useGlobalTheme";

var qbankConfig = require("../config/qbank.js");


export default route(function( /* { store, ssrContext } */ ) {
  const createHistory = process.env.SERVER ?
      createMemoryHistory :
      process.env.VUE_ROUTER_MODE === "history" ?
      createWebHistory :
      createWebHashHistory;

  const Router = createRouter({
      scrollBehavior: () => ({
          left: 0,
          top: 0
      }),
      routes,
      history: createHistory(
          process.env.MODE === "ssr" ? void 0 : qbankConfig.getRouterBase()
      ),
  });

  // Loading state tracking
  let isNavigating = false;
  let loadingTimer = null;

  // Configure LoadingBar to be more prominent
  LoadingBar.setDefaults({
      color: 'primary',
      size: '4px',
      position: 'top'
  });

  // Create simple click-blocking overlay
  const createBlockingOverlay = () => {
      const overlay = document.createElement('div');
      overlay.id = 'navigation-blocker';
      // Theme-matched veil: the light-mode 10% WHITE wash brightens the whole
      // page on every navigation in dark mode (fog flash). Value computed at
      // creation time from the global theme singleton. Safari 12: no `?.`.
      var veil = 'rgba(255, 255, 255, 0.1)';
      var gt = useGlobalTheme();
      if (gt && gt.colorTheme && gt.colorTheme.value === 'theme-dark') {
          veil = 'rgba(0, 0, 0, 0.1)';
      } else if (gt && gt.colorTheme && gt.colorTheme.value === 'theme-sepia') {
          veil = 'rgba(93, 64, 55, 0.08)';
      }
      overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: ${veil};
    z-index: 9998;
    cursor: wait;
    backdrop-filter: blur(1px);
  `;

      // Block all click events
      overlay.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
      });

      document.body.appendChild(overlay);
      return overlay;
  };

  // Remove blocking overlay
  const removeBlockingOverlay = () => {
      const overlay = document.getElementById('navigation-blocker');
      if (overlay) {
          overlay.remove();
      }
  };

  // Start loading with blocking
  const startLoading = () => {
      if (!isNavigating) {
          isNavigating = true;
          LoadingBar.start();
          createBlockingOverlay();

          // Add loading class to body to disable interactions
          document.body.style.pointerEvents = 'none';
          document.body.style.userSelect = 'none';
      }
  };

  // Stop loading and unblock
  const stopLoading = () => {
      // Clear any pending loading timer
      if (loadingTimer) {
          clearTimeout(loadingTimer);
          loadingTimer = null;
      }

      if (isNavigating) {
          LoadingBar.stop();
          removeBlockingOverlay();

          // Re-enable interactions
          document.body.style.pointerEvents = '';
          document.body.style.userSelect = '';

          isNavigating = false;
      }
  };

  // Check if error is related to chunk/module loading
  const isChunkLoadError = (error) => {
      if (!error || !error.message) return false;
      const msg = error.message.toLowerCase();
      return msg.includes('loading chunk') ||
             msg.includes('loading css chunk') ||
             msg.includes('failed to fetch') ||
             msg.includes('dynamically imported module') ||
             msg.includes('unable to preload') ||
             msg.includes('network error') ||
             msg.includes('load failed');
  };

  // A failed chunk after a deploy is EXPECTED: the open tab still names the
  // previous build's files, and one reload fixes it. That reload used to be
  // reported as an error on every occurrence (Sentry QBANK-FRONTEND-15B: 768
  // events, all followed by a successful reload). Now the first failure only
  // leaves a breadcrumb and reloads; it is reported — once, and without another
  // reload, so a missing file can't loop the page — only when it fails AGAIN
  // within CHUNK_RELOAD_WINDOW_MS of that reload. sessionStorage survives the
  // reload; when it is blocked we fall back to the old behaviour (reload).
  const CHUNK_RELOAD_KEY = 'ymp_chunk_reload_at';
  const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

  const readChunkReloadAt = () => {
      try {
          return parseInt(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10) || 0;
      } catch (e) {
          return 0;
      }
  };

  const markChunkReload = () => {
      try {
          window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      } catch (e) {
          // storage blocked — the reload still happens
      }
  };

  const errorMessageOf = (error) => (error && error.message) ? String(error.message) : String(error);

  // Handle errors
  const handleError = (error) => {
      stopLoading();

      if (isChunkLoadError(error)) {
          if (Date.now() - readChunkReloadAt() > CHUNK_RELOAD_WINDOW_MS) {
              if (Sentry && Sentry.addBreadcrumb) {
                  Sentry.addBreadcrumb({
                      category: 'navigation',
                      level: 'warning',
                      message: 'chunk failed, reloading once: ' + errorMessageOf(error),
                  });
              }
              markChunkReload();
              Notify.create({
                  type: 'negative',
                  message: 'Loading failed. Refreshing page...',
                  timeout: 2000
              });

              setTimeout(() => {
                  window.location.reload();
              }, 2000);
              return;
          }

          // Already reloaded for this a moment ago and it still fails: real.
          if (Sentry && Sentry.captureException) {
              Sentry.captureException(new Error('Chunk still failing after reload: ' + errorMessageOf(error)), {
                  tags: { errorType: 'chunk_reload_failed' },
                  extra: { currentPath: window.location.pathname }
              });
          }
          Notify.create({
              type: 'negative',
              message: 'تعذّر تحميل الصفحة. تحقق من اتصالك بالإنترنت ثم حدّث الصفحة.',
              timeout: 0,
              actions: [{ label: 'تحديث', color: 'white', handler: () => { window.location.reload(); } }]
          });
          return;
      }

      // Capture error in Sentry
      if (Sentry && Sentry.captureException) {
          Sentry.captureException(error, {
              tags: { errorType: 'navigation' },
              extra: { currentPath: window.location.pathname }
          });
      }

      Notify.create({
          type: 'negative',
          message: 'Navigation failed. Please try again.',
          timeout: 3000
      });
  };

  // Router guards
  Router.beforeEach((to, from, next) => {

      // ============================================================================
      // CANCEL PENDING PRELOADS
      // Part of the route preloading system - cancels any in-progress preloads
      // when user navigates to prevent wasted bandwidth
      // TO REMOVE: Delete this line when removing the preloading feature
      // ============================================================================
      routePreloader.cancelPendingLoads();



      // Only set timer for actual route changes (not query params or hash changes)
      if (to.path !== from.path && to.name !== from.name) {
          // Clear any existing timer
          if (loadingTimer) {
              clearTimeout(loadingTimer);
          }

          // Start timer - only show loading if navigation takes > 250ms
          loadingTimer = setTimeout(() => {
              startLoading();
          }, 250);
      }

      // Your existing logic (unchanged)
      if (to.name == 'qbanktestinterfaceLayout' || to.name == 'qbanktestinterfacePage') {
          document.querySelector("html").style.overflow = "hidden";
          next();
      } else {
          document.querySelector("html").style.overflow = "auto";
          next();
      }

      if (to.name != 'searchPage' && to.name != 'qbanktestinterfacePage' && to.name != 'qbanktestinterfaceLayout') {
          window.sessionStorage.removeItem('searchPageParams');
          window.sessionStorage.removeItem('testQuestionIndexes');
          window.sessionStorage.removeItem('checkedExamTypes');
          window.sessionStorage.removeItem('checkedSubjectTypes');
          window.sessionStorage.removeItem('checkedSystemTypes');
          window.sessionStorage.removeItem('checkedTopicTypes');
      }

      if (to.name != 'testresults' && to.name != 'testanalysis' && to.name != 'qbanktestinterfacePage' && to.name != 'qbanktestinterfaceLayout') {
          window.sessionStorage.removeItem('filtering_model');
          window.sessionStorage.removeItem('testresultsdata');
          window.sessionStorage.removeItem('testQuestionIndexes');
      }

      if (to.name != 'notesPage' && to.name != 'qbanktestinterfacePage' && to.name != 'qbanktestinterfaceLayout') {
          window.sessionStorage.removeItem('notes_testQuestionIndexes');
          window.sessionStorage.removeItem('notePageParams');
      }


      // ============================================================================
      // ROUTE-BASED PRELOADING SYSTEM
      // ============================================================================
      // Purpose: Automatically preloads related pages based on the current route
      //          to eliminate loading delays when users navigate.
      //
      // How it works:
      // 1. When a user navigates to a page (e.g., 'createtest')
      // 2. The system looks up related pages they're likely to visit next
      // 3. It preloads those pages in the background during browser idle time
      // 4. When the user clicks to navigate, the page loads instantly
      //
      // Example flow:
      // - User visits 'createtest' page
      // - System preloads: 'qbanktestinterfacePage', 'searchPage', 'fastmodePage'
      // - When user clicks "Start Test", it loads instantly (no loading bar)
      //
      // TO REMOVE THIS FEATURE:
      // 1. Delete this entire code block below
      // 2. Delete the import statement: import { routePreloader } from "./routePreloader";
      // 3. Delete the routePreloader.cancelPendingLoads() line in beforeEach
      // 4. Delete the router/routePreloader.js file
      // ============================================================================

      // Preload routes related to current route
      if (to.name) {
          // Check if browser supports requestIdleCallback (most modern browsers do)
          // This ensures preloading happens when browser is idle, not affecting performance
          if ('requestIdleCallback' in window) {
              requestIdleCallback(() => {
                  // This function looks up which routes are related to the current route
                  // and starts loading them in the background
                  routePreloader.preloadForRoute(to.name);
              }, {
                  // Timeout of 2 seconds - if browser isn't idle within 2s,
                  // run anyway to ensure preloading happens
                  timeout: 2000
              });
          } else {
              // Fallback for older browsers without requestIdleCallback
              // Wait 500ms then preload (gives current page time to render first)
              setTimeout(() => {
                  routePreloader.preloadForRoute(to.name);
              }, 500);
          }
      }

      // ============================================================================
      // END OF ROUTE-BASED PRELOADING SYSTEM
      // ============================================================================



  });

  Router.afterEach(() => {
      // Small delay to ensure page is loaded, then stop loading
      setTimeout(() => {
          stopLoading();
      }, 100); // Reduced from 200ms since we already have a 250ms delay
  });

  Router.onError((error) => {
      console.error('Router error:', error);
      handleError(error);
  });

  // Global error handlers - chunk loading errors at ANY time, not just during navigation.
  // Sentry shows ChunkLoadError on CSS chunks (e.g. page-collaborative-lobby.css) firing during
  // initial mount when isNavigating=false, so the navigation-only guard misses them.
  // We still cap how often we auto-reload to avoid loops if the chunk is genuinely missing.
  let lastChunkReloadAt = 0;
  const CHUNK_RELOAD_COOLDOWN_MS = 60000; // 1 min between auto-reloads

  const tryChunkReload = (errLike) => {
      if (!isChunkLoadError(errLike)) return false;
      const now = Date.now();
      if (now - lastChunkReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return false; // Already reloaded recently
      lastChunkReloadAt = now;
      handleError(errLike);
      return true;
  };

  window.addEventListener('error', (event) => {
      // Catch <link>/<script> load failures by checking event.target
      const target = event.target;
      if (target && (target.tagName === 'LINK' || target.tagName === 'SCRIPT')) {
          const src = target.href || target.src || '';
          if (src && (src.includes('chunk') || src.includes('/js/') || src.includes('/css/'))) {
              tryChunkReload({ message: 'Loading chunk failed: ' + src });
              return;
          }
      }
      // Fallback: navigation-time chunk error from runtime
      if ((isNavigating || loadingTimer) && isChunkLoadError({ message: event.message })) {
          handleError({ message: event.message });
      }
  }, true); // capture phase to catch resource errors

  window.addEventListener('unhandledrejection', (event) => {
      // Promise rejections from dynamic imports — try to reload regardless of nav state
      if (tryChunkReload(event.reason)) {
          event.preventDefault();
      }
  });

  return Router;
});
